































































import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";

// Enums
import { DataModules } from "@/store/modules/data";
import { BlueprintFieldsTypes } from "@/data/enums/provisioning";

// Types
import { IProduct } from "@/models/products";
import { IState } from "@/store";
import {
  IProductSetupFunctionsForm,
  IProvisionSetupFunction,
  IBlueprint
} from "@/models/provisioning";

@Component({
  components: {
    "configure-field": () =>
      import(
        "@/components/app/admin/catalogue/products/provisioning/configureField.vue"
      )
  },
  computed: {
    ...mapState({
      productScope(state: IState, getters) {
        return getters[`data/${DataModules.CATALOGUE}/scope`](this.productId);
      },
      product(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CATALOGUE,
          scope: this.productScope
        });
      },

      blueprintSetupFunctionsScope(state: IState, getters) {
        return getters[
          `data/${DataModules.PROVISIONING_BLUEPRINTS}/blueprintSetupFunctionsScope`
        ](this.blueprintId);
      },
      blueprintSetupFunctions(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.PROVISIONING_BLUEPRINTS,
          scope: this.blueprintSetupFunctionsScope
        });
      }
    })
  }
})
export default class ManageSetupFunctionsModal extends Mixins(
  RequestPromises,
  HasValidation
) {
  // Getters
  public product!: IProduct;
  public productScope!: string;

  public blueprintSetupFunctionsScope!: string;
  public blueprintSetupFunctions!: IProvisionSetupFunction[];

  // Props
  @Prop({ required: true })
  public productId!: IProduct["id"];

  @Prop({ required: true })
  public blueprintId!: IBlueprint["id"];

  @Prop({ required: true })
  public setupFunctions!: IProvisionSetupFunction[];

  // Data

  public guardFunctionalities = [
    "staff_get_product",
    "list_provision_blueprint_functions"
  ];

  public form: IProductSetupFunctionsForm = {
    provision_setup_functions: []
  };
  public formClone: IProductSetupFunctionsForm = {
    provision_setup_functions: []
  };

  public isLoading = false;
  public isReloading = false;
  public requestError = false;
  public isProcessing = false;

  public created() {
    if (!this.isContentAvailable) return;

    this.isLoading = true;
    this.loadData();
  }

  public async loadData() {
    try {
      await Promise.all([
        this.getProductData(this.isReloading),
        this.getSetupFunctions(this.isReloading)
      ]);
      this.initiateForm();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getProductData(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/get`, {
      id: this.productId,
      scope: this.productScope,
      vm: this,
      ignoreStored
    });
  }

  public getSetupFunctions(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_BLUEPRINTS}/getSetupFunctions`,
      {
        id: this.blueprintId,
        scope: this.blueprintSetupFunctionsScope,
        params: {
          "filter[is_setup]": 1
        },
        vm: this,
        ignoreStored
      }
    );
  }

  public field(productId) {
    return {
      id: productId,
      field_label: this.$t("_.setup_function"),
      field_type: BlueprintFieldsTypes.SELECT,
      name: this.$t("_.setup_function"),
      options: this.$_.concat(
        [
          {
            label: this.$t("_.no_setup_function"),
            value: "none"
          }
        ],
        this.$_.map(
          this.blueprintSetupFunctions,
          (setupFunction: IProvisionSetupFunction) => {
            return {
              label: setupFunction.label,
              value: setupFunction.id
            };
          }
        )
      )
    };
  }

  public initiateForm() {
    this.$set(
      this.form,
      "provision_setup_functions",
      this.$_.map(this.configurationProducts, product => {
        return {
          product_id: product.id,
          blueprint_function_id: this.$_.get(
            this.$_.find(this.setupFunctions, ["product_id", product.id]),
            "blueprint_function_id",
            null
          )
        };
      })
    );
    this.formClone = this.$_.cloneDeep(this.form);
  }

  public async validateBeforeSubmit() {
    if (!this.canSave) return;

    try {
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      this.isProcessing = true;
      await this.saveSetupFunctions();

      this.$buefy.toast.open({
        message: this.$t(`_.object_successfully_updated`, {
          object: this.$t("_.setup_functions")
        }) as string
      });

      this.$emit("success", true);
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }

    this.isProcessing = false;
  }

  public saveSetupFunctions() {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING}/updateProductSetupFunctions`,
      {
        id: this.productId,
        data: this.form
      }
    );
  }

  public get buttonIsDisabled() {
    return this.$_.isEqual(this.form, this.formClone) || !this.canSave;
  }

  public get configurationProducts() {
    return [
      this.product,
      ...this.$_.concat(
        this.product.products_options,
        this.product.products_attributes
      )
    ];
  }

  public get canSave() {
    return this.$userCan("update_product_provision_setup_functions");
  }

  public get isContentAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.CATALOGUE,
      scope: this.productScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_BLUEPRINTS,
      scope: this.blueprintSetupFunctionsScope,
      vm: this
    });
  }
}
